import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Instagram, Mail, MessageCircle, Send } from "lucide-react";
import { SERVICES, WHATSAPP_URL, EMAIL, INSTAGRAM_URL } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

const initialForm = { name: "", phone: "", email: "", service: "", budget: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("¡Mensaje enviado! Te respondemos en menos de 2 horas laborables.");
      setForm(initialForm);
    } catch {
      toast.error("No se pudo enviar. Escríbenos directamente por WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-all focus:border-[#1D4ED8] focus:ring-4 focus:ring-[#1D4ED8]/10";

  return (
    <section id="contacto" className="relative py-24 sm:py-32">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 lg:grid-cols-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="lg:col-span-5"
        >
          <p className="mb-4 font-mono-sig text-xs font-bold uppercase tracking-[0.3em] text-[#1D4ED8]">
            ( 05 ) — Contacto
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">
            Hablemos de tu proyecto hoy.
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-slate-600">
            Cuéntanos qué necesita tu negocio y te damos una propuesta clara, con precio cerrado,
            en menos de 2 horas laborables.
          </p>

          <div className="mt-10 space-y-4">
            <a
              data-testid="contact-whatsapp-direct"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 transition-all hover:border-[#25D366] hover:shadow-lg"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#25D366]/10 text-[#25D366]">
                <MessageCircle className="h-5 w-5" />
              </span>
              <div>
                <p className="font-display font-bold text-slate-900">WhatsApp directo</p>
                <p className="text-sm text-slate-500">Respuesta en menos de 2 h laborables</p>
              </div>
            </a>
            <a
              data-testid="contact-email-link"
              href={`mailto:${EMAIL}`}
              className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 transition-all hover:border-[#1D4ED8] hover:shadow-lg"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1D4ED8]/10 text-[#1D4ED8]">
                <Mail className="h-5 w-5" />
              </span>
              <div>
                <p className="font-display font-bold text-slate-900">{EMAIL}</p>
                <p className="text-sm text-slate-500">Para propuestas detalladas</p>
              </div>
            </a>
            <a
              data-testid="contact-instagram-link"
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 transition-all hover:border-[#FF9F00] hover:shadow-lg"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#FF9F00]/10 text-[#FF9F00]">
                <Instagram className="h-5 w-5" />
              </span>
              <div>
                <p className="font-display font-bold text-slate-900">@socify_marketing</p>
                <p className="text-sm text-slate-500">Mira nuestro trabajo en Instagram</p>
              </div>
            </a>
          </div>
        </motion.div>

        <motion.form
          data-testid="contact-form"
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
          className="rounded-3xl border border-slate-200 bg-white p-8 shadow-2xl shadow-slate-900/5 sm:p-10 lg:col-span-7"
        >
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label className="mb-2 block font-mono-sig text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                Nombre *
              </label>
              <input data-testid="contact-input-name" required value={form.name} onChange={set("name")} placeholder="Tu nombre" className={inputCls} />
            </div>
            <div>
              <label className="mb-2 block font-mono-sig text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                Teléfono / WhatsApp *
              </label>
              <input data-testid="contact-input-phone" required value={form.phone} onChange={set("phone")} placeholder="+34 600 000 000" className={inputCls} />
            </div>
            <div>
              <label className="mb-2 block font-mono-sig text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                Email *
              </label>
              <input data-testid="contact-input-email" required type="email" value={form.email} onChange={set("email")} placeholder="tu@empresa.es" className={inputCls} />
            </div>
            <div>
              <label className="mb-2 block font-mono-sig text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                Servicio de interés *
              </label>
              <select data-testid="contact-select-service" required value={form.service} onChange={set("service")} className={inputCls}>
                <option value="" disabled>Elige un servicio</option>
                {SERVICES.map((s) => (
                  <option key={s.id} value={s.name}>{s.name}</option>
                ))}
                <option value="Varios / Otro">Varios / Otro</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="mb-2 block font-mono-sig text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                Presupuesto aproximado
              </label>
              <input data-testid="contact-input-budget" value={form.budget} onChange={set("budget")} placeholder="Ej. 500 € – 1.000 €" className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-2 block font-mono-sig text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                Cuéntanos tu proyecto *
              </label>
              <textarea data-testid="contact-input-message" required rows={4} value={form.message} onChange={set("message")} placeholder="Qué vendes, dónde estás y qué quieres conseguir…" className={`${inputCls} resize-none`} />
            </div>
          </div>
          <button
            data-testid="contact-form-submit"
            type="submit"
            disabled={sending}
            className="mt-8 flex w-full items-center justify-center gap-2.5 rounded-full bg-slate-900 py-4 text-sm font-bold text-white transition-all hover:bg-[#1D4ED8] disabled:opacity-60"
          >
            <Send className="h-4 w-4" />
            {sending ? "Enviando…" : "Enviar mensaje"}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
